import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { AlertTriangle } from 'lucide-react';
import { MainLayout } from '../../components/layout/MainLayout';
import {
  orderService,
  OrderSearchFilter,
  OrderSearchResult,
  FilterOptions
} from '../../api/services/orderService';
import { returnService, ReturnAuthorization } from '../../api/services/returnService';
import { supportTicketService, SupportTicket } from '../../api/services/supportTicketService';

type ViewMode = 'open' | 'shipped' | 'returns' | 'support';

const OrdersDashboard: React.FC = () => {
  const navigate = useNavigate();

  // View mode state
  const [viewMode, setViewMode] = useState<ViewMode>('open');

  // Filter state
  const [filter, setFilter] = useState<OrderSearchFilter>({
    IsShippedMode: false,
    CustomerId: null,
    CustomerPo: '',
    ItemState: undefined,
    InvoiceNumber: undefined,
    ShipToName: '',
    ShipToAddress: '',
    OrderBy: 'OrderDate',
    Page: 1,
    PageSize: 10000
  });

  // Debug: Log filter changes
  useEffect(() => {
    console.log('Filter state changed:', filter);
  }, [filter]);

  // Data state
  const [orders, setOrders] = useState<OrderSearchResult[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [atCutterCount, setAtCutterCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Dropdown options
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({
    customers: [],
    shipMethods: [],
    itemStates: []
  });

  // Returns state
  const [returns, setReturns] = useState<ReturnAuthorization[]>([]);
  const [filteredReturns, setFilteredReturns] = useState<ReturnAuthorization[]>([]);
  const [returnsLoading, setReturnsLoading] = useState<boolean>(false);
  const [returnsTotal, setReturnsTotal] = useState<number>(0);
  const [returnsSearch, setReturnsSearch] = useState<string>('');

  // Support tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState<boolean>(false);
  const [ticketStateFilter, setTicketStateFilter] = useState<number>(0);
  const [resolutionModal, setResolutionModal] = useState<{ ticket: SupportTicket; targetState: number } | null>(null);
  const [resolutionText, setResolutionText] = useState('');
  const [openTicketCount, setOpenTicketCount] = useState<number>(0);

  // Load filter options on mount
  useEffect(() => {
    loadFilterOptions();
  }, []);

  // Search on mount and when view mode changes (for orders views)
  useEffect(() => {
    if (viewMode === 'returns' || viewMode === 'support') return; // Don't search orders in non-order views

    const performSearch = async () => {
      setLoading(true);
      try {
        const result = await orderService.searchOrders(filter);
        setOrders(result.result);
        setTotalCount(result.totalCount);
        setAtCutterCount(result.atCutterCount);
        setTotalPages(result.totalPages);
        setCurrentPage(result.page);
      } catch (error) {
        console.error('Error searching orders:', error);
        setOrders([]);
        setTotalCount(0);
        setAtCutterCount(0);
        setTotalPages(1);
        setCurrentPage(1);
      } finally {
        setLoading(false);
      }
    };
    performSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filter.IsShippedMode]);

  const loadFilterOptions = async () => {
    try {
      const options = await orderService.getFilterOptions();
      console.log('Loaded filter options:', options);
      setFilterOptions(options);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadOpenReturns = async () => {
    setReturnsLoading(true);
    try {
      const result = await returnService.searchReturns({ status: 'open' });
      setReturns(result.returns);
      setFilteredReturns(result.returns);
      setReturnsTotal(result.total);
    } catch (error) {
      console.error('Error loading returns:', error);
      setReturns([]);
      setFilteredReturns([]);
      setReturnsTotal(0);
    } finally {
      setReturnsLoading(false);
    }
  };

  // Filter returns based on search term
  const filterReturns = (searchTerm: string) => {
    setReturnsSearch(searchTerm);
    if (!searchTerm.trim()) {
      setFilteredReturns(returns);
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    const filtered = returns.filter((ret) =>
      ret.AuthorizationNumber?.toLowerCase().includes(term) ||
      ret.OrderId?.toString().includes(term) ||
      ret.RecipientName?.toLowerCase().includes(term) ||
      ret.ShipToName?.toLowerCase().includes(term)
    );
    setFilteredReturns(filtered);
  };

  // Load returns when switching to returns view
  useEffect(() => {
    if (viewMode === 'returns') {
      loadOpenReturns();
      setReturnsSearch(''); // Clear search when switching views
    }
  }, [viewMode]);

  const loadTickets = async (state: number) => {
    setTicketsLoading(true);
    try {
      const data = await supportTicketService.getTickets(state);
      setTickets(data);
      if (state === 0) setOpenTicketCount(data.length);
    } catch (error) {
      console.error('Error loading support tickets:', error);
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    supportTicketService.getTickets(0).then(data => setOpenTicketCount(data.length)).catch(() => {});
  }, []);

  useEffect(() => {
    if (viewMode === 'support') {
      loadTickets(ticketStateFilter);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, ticketStateFilter]);

  const handleSetState = async () => {
    if (!resolutionModal) return;
    try {
      await supportTicketService.setState(resolutionModal.ticket.Id, resolutionModal.targetState, resolutionText);
      setResolutionModal(null);
      setResolutionText('');
      loadTickets(ticketStateFilter);
    } catch (error) {
      console.error('Error setting ticket state:', error);
    }
  };

  const needsAttention = (ticket: SupportTicket) => {
    const thirtyMin = 30 * 60 * 1000;
    return Date.now() - new Date(ticket.Modified).getTime() > thirtyMin;
  };

  const stateLabel = (state: number) => ['New', 'Pending', 'Complete'][state] ?? 'Unknown';

  const search = async (overrides?: Partial<OrderSearchFilter>) => {
    const activeFilter = { ...filter, ...overrides };
    setLoading(true);
    try {
      const result = await orderService.searchOrders(activeFilter);
      setOrders(result.result);
      setTotalCount(result.totalCount);
      setAtCutterCount(result.atCutterCount);
      setTotalPages(result.totalPages);
      setCurrentPage(result.page);
    } catch (error) {
      console.error('Error searching orders:', error);
      setOrders([]);
      setTotalCount(0);
      setAtCutterCount(0);
      setTotalPages(1);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  const goToPage = (page: number) => {
    setFilter(prev => ({ ...prev, Page: page }));
    search({ Page: page });
  };

  const clearFilters = () => {
    setFilter({
      IsShippedMode: filter.IsShippedMode,
      CustomerId: null,
      CustomerPo: '',
      ItemState: undefined,
      InvoiceNumber: undefined,
      ShipToName: '',
      ShipToAddress: '',
      OrderBy: 'OrderDate',
      Page: 1,
      PageSize: viewMode === 'open' ? 10000 : 50
    });
  };

  const handleSort = (field: string) => {
    setFilter(prev => ({ ...prev, OrderBy: field, Page: 1 }));
    search({ OrderBy: field, Page: 1 });
  };

  const getPageRange = (current: number, total: number): (number | '...')[] => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '...', total];
    if (current >= total - 3) return [1, '...', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '...', current - 1, current, current + 1, '...', total];
  };

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    if (mode === 'open') {
      setFilter((prevFilter) => ({ ...prevFilter, IsShippedMode: false, Page: 1, PageSize: 10000 }));
    } else if (mode === 'shipped') {
      setFilter((prevFilter) => ({ ...prevFilter, IsShippedMode: true, Page: 1, PageSize: 50 }));
      setOrders([]);
    }
  };

  // Search on mount excluded from support view
  // (support view manages its own data via loadTickets)

  const handleReturnClick = (returnId: number) => {
    navigate(`/production/returns/${returnId}`);
  };

  const handleOrderClick = (orderId: number) => {
    navigate(`/production/orders/${orderId}`);
  };

  return (
    <MainLayout>
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Production Viewer
          </h2>

          {/* View Mode Toggle */}
          <div className="flex gap-2">
            <button
              onClick={() => handleViewModeChange('open')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'open'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Open
            </button>
            <button
              onClick={() => handleViewModeChange('shipped')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'shipped'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Shipped
            </button>
            <button
              onClick={() => handleViewModeChange('returns')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'returns'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Returns/Repairs
            </button>
            <button
              onClick={() => handleViewModeChange('support')}
              className={`relative px-4 py-2 rounded-lg transition-colors ${
                viewMode === 'support'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              Support Tickets
              {openTicketCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center leading-none">
                  {openTicketCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Summary */}
        {viewMode === 'open' && totalCount > 0 && (
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex items-center gap-8">
            <span className="font-bold">OPEN Orders in Production ({totalCount} total)</span>
            <span className="font-bold">Uncut items: {atCutterCount}</span>
          </div>
        )}

        {/* Summary for Shipped Orders */}
        {viewMode === 'shipped' && totalCount > 0 && (
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 flex items-center gap-8">
            <span className="font-bold">
              Shipped Orders: {totalCount} total
              {!filter.CustomerPo && !filter.InvoiceNumber && !filter.ShipToName && !filter.ShipToAddress
                ? ' (Last 30 days)'
                : ' (All dates)'}
            </span>
            {totalPages > 1 && (
              <span>Page {currentPage} of {totalPages}</span>
            )}
          </div>
        )}

        {/* Summary and Search for Returns/Repairs */}
        {viewMode === 'returns' && (
          <div className="mb-4">
            <div className="flex items-center justify-between gap-4">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-bold">
                  Open Returns/Repairs ({filteredReturns.length}{returnsSearch ? ` of ${returnsTotal}` : ''} total)
                </span>
              </div>
              <div className="flex-1 max-w-md">
                <input
                  type="text"
                  value={returnsSearch}
                  onChange={(e) => filterReturns(e.target.value)}
                  placeholder="Search by RMA #, SO #, or Customer..."
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>
            </div>
          </div>
        )}

        {/* Filters - hide for returns and support views */}
        {viewMode !== 'returns' && viewMode !== 'support' && (
        <form
          className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            search();
          }}
        >
          {/* Customer Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Customer
            </label>
            <select
              value={filter.CustomerId || ''}
              onChange={(e) => {
                const newCustomerId = e.target.value || null;
                console.log('Customer selected:', e.target.value, 'Parsed:', newCustomerId);
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  CustomerId: newCustomerId
                }));
              }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All</option>
              {filterOptions.customers.map((customer) => (
                <option key={customer.CustomerId} value={customer.CustomerId}>
                  {customer.CustomerName}
                </option>
              ))}
            </select>
          </div>

          {/* PO Number Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              PO Number
            </label>
            <input
              type="text"
              value={filter.CustomerPo || ''}
              onChange={(e) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  CustomerPo: e.target.value
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Enter PO"
            />
          </div>

          {/* At Station Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              At Station
            </label>
            <select
              value={filter.ItemState != null ? filter.ItemState.toString() : ''}
              onChange={(e) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  ItemState: e.target.value ? parseInt(e.target.value) : undefined
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">All</option>
              {filterOptions.itemStates.map((state) => (
                <option key={state.ActionId} value={state.ActionId.toString()}>
                  {state.ActionName}
                </option>
              ))}
            </select>
          </div>

          {/* SO Number Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SO Number
            </label>
            <input
              type="number"
              value={filter.InvoiceNumber != null ? filter.InvoiceNumber.toString() : ''}
              onChange={(e) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  InvoiceNumber: e.target.value ? parseInt(e.target.value) : undefined
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Enter SO"
            />
          </div>

          {/* ShipToName Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ship To Name
            </label>
            <input
              type="text"
              value={filter.ShipToName || ''}
              onChange={(e) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  ShipToName: e.target.value
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Search name"
            />
          </div>

          {/* ShipToAddress Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Ship To Address
            </label>
            <input
              type="text"
              value={filter.ShipToAddress || ''}
              onChange={(e) =>
                setFilter((prevFilter) => ({
                  ...prevFilter,
                  ShipToAddress: e.target.value
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="Search address"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-end gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Submit
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                clearFilters();
              }}
              className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
            >
              Clear
            </button>
          </div>
        </form>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-6">
        {/* Returns/Repairs View */}
        {viewMode === 'returns' ? (
          returnsLoading ? (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-600 dark:text-gray-400">Loading returns...</p>
            </div>
          ) : filteredReturns.length === 0 ? (
            <div className="text-center py-8 text-gray-600 dark:text-gray-400">
              {returnsSearch ? 'No returns match your search' : 'No open returns/repairs found'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">RMA #</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">SO #</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Type</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Tag</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Customer</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Reason</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Created</th>
                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredReturns.map((ret) => (
                    <tr
                      key={ret.Id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                      onClick={() => handleReturnClick(ret.Id)}
                    >
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <span className="font-bold text-blue-600 dark:text-blue-400">
                          {ret.AuthorizationNumber}
                        </span>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                        {ret.OrderId}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        <span className={`px-2 py-1 rounded text-sm font-medium ${
                          ret.ReturnType === 1
                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }`}>
                          {ret.ReturnType === 1 ? 'Repair' : 'Return'}
                        </span>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                        {ret.ReturnTag}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                        <div>{ret.RecipientName}</div>
                        {ret.ShipToName && ret.ShipToName !== ret.RecipientName && (
                          <div className="text-sm text-gray-500">{ret.ShipToName}</div>
                        )}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                        <div className="max-w-xs truncate" title={ret.Description}>
                          {ret.Description}
                        </div>
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                        {format(new Date(ret.CreateDate), 'M/d/yyyy')}
                      </td>
                      <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                        {ret.ReceiveDate ? (
                          <span className="px-2 py-1 rounded text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                            Received
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded text-sm font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            Pending
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : viewMode === 'support' ? (
          /* Support Tickets View */
          <div>
            {/* Filter radio */}
            <div className="flex gap-6 mb-4">
              {([{ label: 'New', value: 0 }, { label: 'Pending', value: 1 }] as { label: string; value: number }[]).map(({ label, value }) => (
                <label key={label} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="radio"
                    name="ticketState"
                    checked={ticketStateFilter === value}
                    onChange={() => setTicketStateFilter(value)}
                    className="accent-purple-600"
                  />
                  {label}
                </label>
              ))}
            </div>

            {ticketsLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <p className="mt-2 text-gray-600 dark:text-gray-400">Loading tickets...</p>
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8 text-gray-600 dark:text-gray-400">No tickets found</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-sm">
                  <thead>
                    <tr className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left whitespace-nowrap">Create Date</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left">Ticket #</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left">Customer Name</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left">Contact</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left">History</th>
                      <th className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr key={ticket.Id} className="align-top hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                          <div>{format(new Date(ticket.Modified), 'M/d/yy h:mm a')}</div>
                          {needsAttention(ticket) && (
                            <span className="inline-flex items-center gap-1 mt-1 text-xs text-amber-600 dark:text-amber-400">
                              <AlertTriangle size={12} /> Needs attention
                            </span>
                          )}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">
                          {ticket.Id}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">
                          {ticket.Name}
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-900 dark:text-gray-100">
                          <div>{ticket.Phone}</div>
                          <div className="text-gray-500 dark:text-gray-400">{ticket.Email}</div>
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          <table className="w-full text-xs border border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                            <tbody>
                              {/* Initial create entry */}
                              <tr className="bg-gray-50 dark:bg-gray-700/50">
                                <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 whitespace-nowrap">{format(new Date(ticket.Modified), 'M/d/yy h:mm a')}</td>
                                <td className="border border-gray-200 dark:border-gray-600 px-2 py-1">Create</td>
                                <td className="border border-gray-200 dark:border-gray-600 px-2 py-1">{ticket.ModifiedBy}</td>
                                <td className="border border-gray-200 dark:border-gray-600 px-2 py-1">
                                  {ticket.OrderId ? (
                                    <a href={`/production/orders/${ticket.OrderId}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline font-medium">
                                      SO #{ticket.OrderId}
                                    </a>
                                  ) : '—'}
                                </td>
                                <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 max-w-xs">{ticket.Comment}</td>
                              </tr>
                              {ticket.Transitions.map((t) => (
                                <tr key={t.Id}>
                                  <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 whitespace-nowrap">{format(new Date(t.Modified), 'M/d/yy h:mm a')}</td>
                                  <td className="border border-gray-200 dark:border-gray-600 px-2 py-1">{stateLabel(t.State)}</td>
                                  <td className="border border-gray-200 dark:border-gray-600 px-2 py-1">{t.ModifiedBy}</td>
                                  <td className="border border-gray-200 dark:border-gray-600 px-2 py-1">—</td>
                                  <td className="border border-gray-200 dark:border-gray-600 px-2 py-1 max-w-xs">{t.Resolution}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                        <td className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                          {ticket.State === 0 && (
                            <button
                              onClick={() => { setResolutionModal({ ticket, targetState: 1 }); setResolutionText(''); }}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs whitespace-nowrap"
                            >
                              Set Pending
                            </button>
                          )}
                          {ticket.State === 1 && (
                            <button
                              onClick={() => { setResolutionModal({ ticket, targetState: 2 }); setResolutionText(''); }}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 text-xs whitespace-nowrap"
                            >
                              Set Complete
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Loading orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-600 dark:text-gray-400">
            No orders found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
              <thead>
                <tr className="bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <button
                      onClick={() => handleSort('OrderDate')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      Date
                    </button>
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <button
                      onClick={() => handleSort('EstShipDate')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      ESD
                    </button>
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <button
                      onClick={() => handleSort('OrderId')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      SO #
                    </button>
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <button
                      onClick={() => handleSort('BillToName')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      Customer & Ship Info
                    </button>
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <button
                      onClick={() => handleSort('CustomerOrderNo')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      PO #
                    </button>
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                    <button
                      onClick={() => handleSort('ShipVia')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      Shipping Method
                    </button>
                  </th>
                  <th className="border border-gray-300 dark:border-gray-600 px-4 py-2" colSpan={2}>
                    <button
                      onClick={() => handleSort('ItemCount')}
                      className="text-blue-600 dark:text-blue-300 hover:underline font-semibold"
                    >
                      Items
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr
                    key={order.OrderId}
                    className={`${
                      order.DaysFromShip < 3
                        ? 'bg-yellow-50 dark:bg-yellow-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {/* Date */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      <div className="font-bold">
                        {format(new Date(order.OrderDate), 'M/d/yyyy')}
                      </div>
                      <div
                        className={`text-sm ${
                          order.Age > 7 ? 'text-red-600 dark:text-red-400' : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {order.Age} days old
                      </div>
                    </td>

                    {/* ESD */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-2">
                        {order.DaysFromShip < 3 && (
                          <span className="text-yellow-600 dark:text-yellow-400">⚠</span>
                        )}
                        <span
                          className={`font-bold ${
                            order.Age > 7
                              ? 'text-red-600 dark:text-red-400'
                              : order.Age > 3 && order.Age <= 7
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {format(new Date(order.EstShipDate), 'M/d/yyyy')}
                        </span>
                      </div>
                    </td>

                    {/* SO # */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                      <a
                        href={`/production/orders/${order.OrderId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-bold text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        {order.OrderId}
                      </a>
                    </td>

                    {/* Customer & Ship Info */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      <div>{order.CustomerId}</div>
                      <div>{order.ShipToName}</div>
                      <div>{order.ShipVia}</div>
                      <div className="font-bold">
                        DS: {order.DropShip ? 'Yes' : 'No'}
                      </div>
                    </td>

                    {/* PO # */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      {order.CustomerOrderNo}
                    </td>

                    {/* Shipping Method */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      {order.ShipVia}
                    </td>

                    {/* Item Count & Total */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      <div>{order.ItemCount}</div>
                      <div>${order.Total.toFixed(2)}</div>
                    </td>

                    {/* Items */}
                    <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-gray-900 dark:text-gray-100">
                      {viewMode === 'open' && (
                        <dl className="space-y-1">
                          {order.Items.map((item, idx) => (
                            <div key={idx} className="text-sm">
                              <dt className="font-medium">{item.PartNumber}</dt>
                              <dd className="text-gray-600 dark:text-gray-400">
                                {item.Status}
                              </dd>
                            </div>
                          ))}
                        </dl>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {viewMode !== 'open' && totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {(currentPage - 1) * (filter.PageSize ?? 50) + 1}–{Math.min(currentPage * (filter.PageSize ?? 50), totalCount)} of {totalCount} orders
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-1.5 rounded text-sm ${
                      currentPage === 1
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    ‹ Prev
                  </button>

                  {getPageRange(currentPage, totalPages).map((p, i) =>
                    p === '...' ? (
                      <span key={`ellipsis-${i}`} className="px-2 py-1.5 text-sm text-gray-400 dark:text-gray-500">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => goToPage(p as number)}
                        className={`px-3 py-1.5 rounded text-sm ${
                          p === currentPage
                            ? 'bg-blue-600 text-white font-semibold'
                            : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-1.5 rounded text-sm ${
                      currentPage === totalPages
                        ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                    }`}
                  >
                    Next ›
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Resolution modal for support tickets */}
    {resolutionModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Set {stateLabel(resolutionModal.targetState)} — Ticket #{resolutionModal.ticket.Id}
            </h2>
            <button
              onClick={() => setResolutionModal(null)}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl leading-none"
            >
              ×
            </button>
          </div>
          <div className="px-6 py-5">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Comment <span className="text-red-500">*</span>
            </label>
            <textarea
              value={resolutionText}
              onChange={(e) => setResolutionText(e.target.value)}
              rows={5}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          </div>
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSetState}
              disabled={!resolutionText.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Save
            </button>
            <button
              onClick={() => setResolutionModal(null)}
              className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-sm rounded hover:bg-gray-300 dark:hover:bg-gray-500"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    )}
    </MainLayout>
  );
};

export default OrdersDashboard;
